import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tag, Plus, Pencil, Trash2, LogOut, Copy, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCoupons({ user, setUser }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchCoupons();
  }, [user, navigate]);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/coupons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setCoupons(data.coupons || []);
    } catch (error) {
      toast.error('Error al cargar cupones');
    } finally {
      setLoading(false);
    }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/coupons/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Cupón eliminado');
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/coupons/${coupon.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !coupon.is_active })
      });
      if (response.ok) {
        toast.success(coupon.is_active ? 'Cupón desactivado' : 'Cupón activado');
        fetchCoupons();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-coupons-page">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesion
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6 overflow-x-auto">
            <Link to="/admin" className="text-gray-600 hover:text-black whitespace-nowrap">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black whitespace-nowrap">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black whitespace-nowrap">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black whitespace-nowrap">Clientes</Link>
            <Link to="/admin/coupons" className="font-medium text-black border-b-2 border-black pb-2 whitespace-nowrap">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black whitespace-nowrap">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black whitespace-nowrap">Envios</Link>
            <Link to="/admin/contact-requests" className="text-gray-600 hover:text-black whitespace-nowrap">Solicitudes</Link>
            <Link to="/admin/notices" className="text-gray-600 hover:text-black whitespace-nowrap">Avisos</Link>
            <Link to="/admin/newsletter" className="text-gray-600 hover:text-black whitespace-nowrap">Newsletter</Link>
            <Link to="/admin/gallery" className="text-gray-600 hover:text-black whitespace-nowrap">Galeria</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display' }}>Cupones Promocionales</h2>
          <Button
            data-testid="create-coupon-btn"
            onClick={() => { setEditingCoupon(null); setShowForm(true); }}
            className="bg-black text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Crear Cupon
          </Button>
        </div>

        {showForm && (
          <CouponForm
            coupon={editingCoupon}
            onClose={() => { setShowForm(false); setEditingCoupon(null); }}
            onSuccess={() => { setShowForm(false); setEditingCoupon(null); fetchCoupons(); }}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 border-2 border-black">
            <p className="text-sm text-gray-600">Total Cupones</p>
            <p className="text-2xl font-bold">{coupons.length}</p>
          </div>
          <div className="bg-white p-4 border-2 border-black">
            <p className="text-sm text-gray-600">Activos</p>
            <p className="text-2xl font-bold text-green-600">{coupons.filter(c => c.is_active).length}</p>
          </div>
          <div className="bg-white p-4 border-2 border-black">
            <p className="text-sm text-gray-600">Usos Totales</p>
            <p className="text-2xl font-bold">{coupons.reduce((s, c) => s + (c.used_count || 0), 0)}</p>
          </div>
        </div>

        {/* Coupon Table */}
        <div className="bg-white border-2 border-black">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left p-4">Codigo</th>
                  <th className="text-left p-4">Tipo</th>
                  <th className="text-left p-4">Valor</th>
                  <th className="text-left p-4">Min. Pedido</th>
                  <th className="text-left p-4">Aplica a</th>
                  <th className="text-left p-4">Usos</th>
                  <th className="text-left p-4">Estado</th>
                  <th className="text-right p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-8 text-center text-gray-500">
                      <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No hay cupones creados
                    </td>
                  </tr>
                ) : (
                  coupons.map((coupon) => (
                    <tr key={coupon.id} className="border-b hover:bg-gray-50" data-testid={`coupon-row-${coupon.code}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold bg-gray-100 px-2 py-1 rounded">{coupon.code}</span>
                          <button
                            onClick={() => { navigator.clipboard.writeText(coupon.code); toast.success('Codigo copiado'); }}
                            className="text-gray-400 hover:text-black"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4 text-sm">
                        {coupon.discount_type === 'percentage' ? 'Porcentaje' : 'Importe fijo'}
                      </td>
                      <td className="p-4 font-bold">
                        {coupon.discount_type === 'percentage'
                          ? `${coupon.discount_value}%`
                          : `${coupon.discount_value.toFixed(2)}\u20AC`}
                      </td>
                      <td className="p-4 text-sm">
                        {coupon.min_order_amount > 0 ? `${coupon.min_order_amount.toFixed(2)}\u20AC` : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        <div className="flex flex-wrap gap-1">
                          {coupon.applicable_brands?.map(b => (
                            <span key={b} className="bg-gray-100 text-xs px-2 py-0.5 rounded">{b}</span>
                          ))}
                          {coupon.applicable_categories?.map(c => (
                            <span key={c} className="bg-blue-50 text-xs px-2 py-0.5 rounded">{c}</span>
                          ))}
                          {!coupon.applicable_brands?.length && !coupon.applicable_categories?.length && (
                            <span className="text-gray-400 text-xs">Todos</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm">
                        {coupon.used_count || 0}{coupon.max_uses > 0 ? ` / ${coupon.max_uses}` : ' / ilimitado'}
                      </td>
                      <td className="p-4">
                        <button
                          data-testid={`toggle-coupon-${coupon.code}`}
                          onClick={() => toggleActive(coupon)}
                          className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                            coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {coupon.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          {coupon.is_active ? 'Activo' : 'Inactivo'}
                        </button>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-coupon-${coupon.code}`}
                            onClick={() => { setEditingCoupon(coupon); setShowForm(true); }}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-coupon-${coupon.code}`}
                            onClick={() => deleteCoupon(coupon.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CouponForm({ coupon, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    code: coupon?.code || '',
    discount_type: coupon?.discount_type || 'percentage',
    discount_value: coupon?.discount_value || '',
    min_order_amount: coupon?.min_order_amount || 0,
    max_uses: coupon?.max_uses || 0,
    max_uses_per_user: coupon?.max_uses_per_user || 0,
    expires_at: coupon?.expires_at ? coupon.expires_at.split('T')[0] : '',
    is_active: coupon?.is_active !== false,
    description: coupon?.description || '',
    applicable_brands: coupon?.applicable_brands || [],
    applicable_categories: coupon?.applicable_categories || []
  });
  const [saving, setSaving] = useState(false);
  const availableBrands = ['Arthur Fitz', 'Walker', 'Walker Tape', 'TrueTape', 'The Professional Hair Labs'];
  const availableCategories = ['protesis', 'mantenimiento'];

  const toggleBrand = (brand) => {
    setFormData(prev => ({
      ...prev,
      applicable_brands: prev.applicable_brands.includes(brand)
        ? prev.applicable_brands.filter(b => b !== brand)
        : [...prev.applicable_brands, brand]
    }));
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      applicable_categories: prev.applicable_categories.includes(cat)
        ? prev.applicable_categories.filter(c => c !== cat)
        : [...prev.applicable_categories, cat]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code.trim()) {
      toast.error('El codigo es obligatorio');
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      toast.error('El valor del descuento debe ser mayor a 0');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        max_uses: parseInt(formData.max_uses) || 0,
        max_uses_per_user: parseInt(formData.max_uses_per_user) || 0,
        applicable_brands: formData.applicable_brands.length > 0 ? formData.applicable_brands : null,
        applicable_categories: formData.applicable_categories.length > 0 ? formData.applicable_categories : null,
        expires_at: formData.expires_at
          ? new Date(formData.expires_at + 'T23:59:59Z').toISOString()
          : null
      };

      const url = coupon
        ? `${BACKEND_URL}/api/admin/coupons/${coupon.id}`
        : `${BACKEND_URL}/api/admin/coupons`;
      const method = coupon ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(coupon ? 'Cupon actualizado' : 'Cupon creado');
        onSuccess();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-6" data-testid="coupon-form-modal">
      <div className="bg-white p-8 border-2 border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            {coupon ? 'Editar Cupon' : 'Nuevo Cupon'}
          </h3>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Codigo del cupon *</Label>
            <Input
              data-testid="coupon-code-input"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="Ej: VERANO25"
              className="font-mono uppercase"
            />
          </div>

          <div>
            <Label>Descripcion</Label>
            <Input
              data-testid="coupon-description-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Ej: Descuento de verano"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Tipo de descuento</Label>
              <select
                data-testid="coupon-type-select"
                className="w-full border-2 border-gray-200 rounded p-2"
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
              >
                <option value="percentage">Porcentaje (%)</option>
                <option value="fixed">Importe fijo (EUR)</option>
              </select>
            </div>
            <div>
              <Label>Valor del descuento *</Label>
              <Input
                data-testid="coupon-value-input"
                type="number"
                step="0.01"
                min="0"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                placeholder={formData.discount_type === 'percentage' ? 'Ej: 15' : 'Ej: 10.00'}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Pedido minimo (EUR)</Label>
              <Input
                data-testid="coupon-min-order-input"
                type="number"
                step="0.01"
                min="0"
                value={formData.min_order_amount}
                onChange={(e) => setFormData({ ...formData, min_order_amount: e.target.value })}
                placeholder="0 = sin minimo"
              />
            </div>
            <div>
              <Label>Usos maximos totales</Label>
              <Input
                data-testid="coupon-max-uses-input"
                type="number"
                min="0"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="0 = ilimitado"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Usos max. por usuario</Label>
              <Input
                data-testid="coupon-max-per-user-input"
                type="number"
                min="0"
                value={formData.max_uses_per_user}
                onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                placeholder="0 = ilimitado"
              />
            </div>
            <div>
              <Label>Fecha de expiracion</Label>
              <Input
                data-testid="coupon-expires-input"
                type="date"
                value={formData.expires_at}
                onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Aplicable a marcas</Label>
            <p className="text-xs text-gray-500 mb-2">Deja sin seleccionar para aplicar a todas las marcas</p>
            <div className="flex flex-wrap gap-2">
              {availableBrands.map(brand => (
                <button
                  key={brand}
                  type="button"
                  onClick={() => toggleBrand(brand)}
                  className={`px-3 py-1.5 text-sm border-2 rounded transition-colors ${
                    formData.applicable_brands.includes(brand)
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                  }`}
                  data-testid={`brand-${brand.replace(/\s/g, '-')}`}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Aplicable a categorias</Label>
            <p className="text-xs text-gray-500 mb-2">Deja sin seleccionar para aplicar a todas las categorias</p>
            <div className="flex flex-wrap gap-2">
              {availableCategories.map(cat => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => toggleCategory(cat)}
                  className={`px-3 py-1.5 text-sm border-2 rounded transition-colors ${
                    formData.applicable_categories.includes(cat)
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-black'
                  }`}
                  data-testid={`category-${cat}`}
                >
                  {cat === 'protesis' ? 'Protesis' : 'Mantenimiento'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="is_active">Cupon activo</Label>
          </div>

          <div className="flex gap-3 pt-2">
            <Button data-testid="save-coupon-btn" type="submit" className="bg-black text-white flex-1" disabled={saving}>
              {saving ? 'Guardando...' : coupon ? 'Guardar Cambios' : 'Crear Cupon'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
